
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/lib/supabase';
import { ProductFormData } from './ProductForm';

interface UseProductFormProps {
  initialData?: ProductFormData;
  onSave: () => void;
  onOpenChange: (open: boolean) => void;
}

export function useProductForm({ initialData, onSave, onOpenChange }: UseProductFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    description: '',
    price: undefined,
    currency: 'USD',
    category: '',
    image_url: '',
    affiliate_link: '',
  });

  // Update form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData.id,
        name: initialData.name || '',
        description: initialData.description || '',
        price: initialData.price,
        currency: initialData.currency || 'USD',
        category: initialData.category || '',
        image_url: initialData.image_url || '',
        affiliate_link: initialData.affiliate_link || '',
        source: initialData.source,
      });
    } else {
      // Reset form for new product
      setFormData({
        name: '',
        description: '',
        price: undefined,
        currency: 'USD',
        category: '',
        image_url: '',
        affiliate_link: '',
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseFloat(value) : undefined) : value,
    }));
  };

  const handleImageUploaded = (imageUrl: string) => {
    setFormData(prev => ({ ...prev, image_url: imageUrl }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    
    if (!formData.affiliate_link.trim()) {
      toast.error('Affiliate link is required');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Determine if we're editing a recommendation or a product
      if (formData.id && formData.source === 'recommendations') {
        // Update existing recommendation
        const { error } = await supabase
          .from('product_recommendations')
          .update({
            name: formData.name,
            description: formData.description,
            price: formData.price,
            currency: formData.currency,
            category: formData.category,
            image_url: formData.image_url,
            affiliate_link: formData.affiliate_link,
            updated_at: new Date().toISOString(),
          })
          .eq('id', formData.id);
        
        if (error) throw error;
        toast.success('Product recommendation updated successfully');
      } else if (formData.id) {
        // Update existing product
        const { error } = await supabase
          .from('products')
          .update({
            name: formData.name,
            description: formData.description,
            price: formData.price,
            currency: formData.currency,
            category: formData.category,
            image_url: formData.image_url,
            affiliate_link: formData.affiliate_link,
            updated_at: new Date().toISOString(),
          })
          .eq('id', formData.id);
        
        if (error) throw error;
        toast.success('Product updated successfully');
      } else {
        // Create new product
        const { error } = await supabase
          .from('products')
          .insert({
            id: uuidv4(),
            name: formData.name,
            description: formData.description,
            price: formData.price,
            currency: formData.currency,
            category: formData.category,
            image_url: formData.image_url,
            affiliate_link: formData.affiliate_link,
          });
        
        if (error) throw error;
        toast.success('Product created successfully');
      }
      
      onSave();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving product:', error);
      toast.error(`Failed to save product: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    isSubmitting,
    handleChange,
    handleImageUploaded,
    handleSubmit
  };
}
